import styled from "styled-components";
import { designTokens, getTypography } from "@/styles/designTokens";
import contactBg from "@/assets/img/contact_bg.png";

interface ButtonBaseProps {
  $isCollapsed?: boolean;
  $isTitle?: boolean;
  $isSubMenuOpen?: boolean;
}

interface SideBarProps {
  $isCollapsed?: boolean;
  $isSubMenuOpen?: boolean;
}

interface SideBarToggleButtonProps {
  $isCollapsed?: boolean;
}

// 사이드바 컨테이너 스타일
export const SideBarContainer = styled.div.attrs((props) => ({
  // DOM 전달 방지
}))<SideBarProps>`
  width: ${(props) => (props.$isCollapsed ? "122px" : "256px")};
  height: 100vh;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  padding: 40px 0px 40px 40px;
  background-color: white;
  position: relative;
  box-shadow: var(
    --clear-box-box-shadow,
    0px 0px 14px 0px rgba(58, 111, 133, 0.2)
  );
  backdrop-filter: var(--clear-box-backdrop-filter, blur(8px));
  flex-shrink: 0;
  transition: all 0.1s ease-in-out;
`;

export const SideBarMenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-self: stretch;
  position: relative;
  width: 160px;
  height: 100%;
`;

export const TopContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

export const LogoContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  justify-content: flex-start;
  height: 36px;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  margin-bottom: 40px;
`;

export const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;
  align-items: flex-start;
  justify-content: flex-start;
  align-self: stretch;
  position: relative;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  /* 스크롤바 숨기기 */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

export const BottomContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  align-items: flex-start;
  position: relative;
  flex-shrink: 0;
`;

export const InquiryContainer = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0px;
  gap: 10px;
  height: 94px;
  background: url(${contactBg}) no-repeat center center;
  background-size: cover;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  align-self: stretch;
`;

export const InquiryText = styled.p`
  color: ${designTokens.colors.font.septenary};
  ${getTypography("korean", "body4")}
  margin: 0;
`;

export const InquiryButton = styled.button`
  padding: 6px 12px;
  background-color: ${designTokens.colors.box.secondary};
  background-image: ${designTokens.colors.line.point};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  ${getTypography("korean", "body3_semibold")}
`;

// 서브 사이드바 컨테이너
export const SideBarSubContainer = styled.div.attrs((props) => ({
  // DOM 전달 방지
}))<SideBarProps>`
  display: flex;
  flex-direction: row;
  padding: 0;
  gap: 22px;
  align-items: flex-start;
  justify-content: flex-start;
  position: relative;
  width: 100%;
  transform-origin: top;
  transform: scaleY(${(props) => (props.$isSubMenuOpen ? 1 : 0)});
  height: ${(props) => (props.$isSubMenuOpen ? "auto" : "0")};
  margin-bottom: ${(props) => (props.$isSubMenuOpen ? "20px" : "0")};
  transition: all 0.2s ease-in-out;
`;

export const SubMenuContainer = styled.div.attrs((props) => ({
  // DOM 전달 방지
}))<SideBarProps>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
  justify-content: center;
  position: relative;
  flex: 1;
  width: 100%;
  opacity: ${(props) => (props.$isSubMenuOpen ? 1 : 0)};
  transition: opacity 0.2s ease-in-out;
  transition-delay: ${(props) => (props.$isSubMenuOpen ? "0.1s" : "0s")};
`;

export const ButtonBase = styled.button.attrs((props) => ({
  // isCollapsed는 DOM에 전달되지 않음
}))<ButtonBaseProps>`
  border-radius: 4px;
  padding: 16px 0px 16px 0px;
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  justify-content: flex-start;
  align-self: stretch;
  position: relative;
  border: none;
  cursor: pointer;
  background-color: transparent;
  color: ${designTokens.colors.font.primary};
  ${getTypography("korean", "body2")}

  span {
    display: ${(props) => (props.$isCollapsed ? "none" : "inline")};
  }
`;

// SideBarButton에서 svg 관련 스타일 제거 (svg는 항상 표시)
export const SideBarButton = styled(ButtonBase)``;

export const SideBarButtonActive = styled(ButtonBase)`
  background: ${designTokens.colors.line.point};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  ${getTypography("korean", "body2_bold")}

  span {
    background: ${designTokens.colors.line.point};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  svg {
    * {
      stroke: url(#sidebarGradient);
      stroke-width: 1;
      /* fill: none.;. */
    }
  }
`;

export const Line = styled.div`
  border-style: solid;
  border-color: ${designTokens.colors.line.button};
  border-width: 0 0 0 1px;
  height: 100%;
  position: relative;
  flex-shrink: 0;
`;

export const SideBarTitleButton = styled(ButtonBase)``;

export const SideBarTitleButtonActive = styled(SideBarTitleButton)`
  span {
    background: ${designTokens.colors.line.point};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    ${getTypography("korean", "body2_bold")}
  }

  svg {
    * {
      stroke: url(#sidebarGradient);
      stroke-width: 1;
    }
  }
`;

// 서브 사이드바 버튼
export const SideBarSubButton = styled(ButtonBase)`
  padding: 4px 0px 4px 10px;
  width: 100%;
  color: ${designTokens.colors.font.quaternary};

  &:hover {
    background-color: #f5f5f5;
  }
`;

export const SideBarSubButtonActive = styled(SideBarSubButton)`
  background-color: #f5f5f5;

  span {
    color: #393939; // 클릭된 상태에서만 quaternary 색상 적용
  }
`;

// 토글 버튼 스타일
export const SideBarToggleButton = styled.div.attrs((props) => ({
  // DOM 전달 방지
}))<SideBarToggleButtonProps>`
  display: flex;
  justify-content: ${(props) =>
    props.$isCollapsed ? "flex-start" : "flex-end"};
  cursor: pointer;

  .arrow-group {
    transform: ${(props) =>
      props.$isCollapsed ? "rotate(180deg)" : "rotate(0deg)"};
    transform-origin: 20px 18.5px;
    transition: transform 0.3s ease-in-out;
  }
`;
